import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const VbGramWordResolutionForm = () => {
  const { language } = useLanguage();
  const componentRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: language === 'marathi' ? 'VB_GRAM_G_Resolution_Word_26Dec2025.pdf' : 'VB_GRAM_G_Resolution_Word_26Dec2025_EN.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          .page {
            width: 210mm;
            height: 297mm;
            padding: 20mm;
            margin: 0 auto 20px auto;
            box-sizing: border-box;
            position: relative;
            background-color: white;
            overflow: hidden;
            box-shadow: 0 0 15px rgba(0,0,0,0.1);
          }
          
          .dashed-line {
            border-bottom: 1px dashed black;
            display: inline-block;
          }

          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              width: 210mm !important;
              height: 297mm !important;
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .page {
              margin: 0 !important;
              box-shadow: none !important;
              width: 210mm !important;
              height: 297mm !important;
              page-break-after: always !important;
              break-after: page !important;
              break-inside: avoid !important;
              border: none !important;
            }
            .no-print {
              display: none !important;
            }
            .min-h-screen {
              min-height: auto !important;
              padding: 0 !important;
            }
          }
        `}
      </style>
      
      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4 no-print">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Print' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Download PDF' : 'Download PDF'}
        </button>
      </div>

      <div ref={componentRef} className="flex flex-col items-center text-black">
        {/* PAGE 1 */}
        <div className="bg-white relative box-border flex flex-col page pt-12">
           <div className="flex justify-center items-center text-[18px] font-bold mb-1 ml-8">
              <span>{language === 'marathi' ? 'कार्यालय ग्रामपंचायत' : 'Gram Panchayat Office'}</span>
              <span className="dashed-line w-48 h-4 mx-1"></span>
              <span>{language === 'marathi' ? 'ता' : 'Tal'}</span>
              <span className="dashed-line w-40 h-4 mx-1"></span>
           </div>
           <div className="flex justify-start items-center text-[18px] font-bold mb-2 ml-12">
              <span className="dashed-line w-12 h-4 mr-1"></span>
              <span>{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
              <span className="dashed-line w-40 h-4 mx-1"></span>
           </div>

           <div className="text-center text-[18px] mb-2 ml-4">
              <span className="font-bold">{language === 'marathi' ? 'दिनांक:- २६/१२/२०२५ रोजी झालेल्या ग्रामसभा बैठकीचे इतिवृत्त नोंदवही' : 'Date:- 26/12/2025 Minutes Register of Gramsabha Meeting'}</span>
           </div>

           <table className="w-full border-collapse border border-black text-[15px]">
              <thead>
                 <tr>
                    <th className="border border-black p-1 w-10 text-center font-normal">{language === 'marathi' ? 'विषय नंबर' : 'Subject No.'}</th>
                    <th className="border border-black p-1 w-10 text-center font-normal">{language === 'marathi' ? 'ठराव नंबर' : 'Resolution No.'}</th>
                    <th className="border border-black p-2 text-center text-[15px] font-normal">{language === 'marathi' ? 'सभेपुढे विचाराकरीता आलेले विषय व ठराव' : 'Subjects and Resolutions brought before the meeting for consideration'}</th>
                    <th className="border border-black p-1 w-24 text-center font-normal">{language === 'marathi' ? 'शेरे आणि तारखेसह ठरावाची अंमल- बजावणी' : 'Implementation of resolution with remarks and date'}</th>
                 </tr>
              </thead>
           </table>

           <div className="border border-black border-t-0 p-3 flex-grow flex flex-col relative text-[17px] leading-snug space-y-1">
              <p className="flex items-end flex-wrap indent-8">
                 <span>{language === 'marathi' ? 'आज दिनांक २६/१२/२०२५ शुक्रवार रोजी ग्रामपंचायत कार्यालय' : 'Today, Friday, 26/12/2025, at Gram Panchayat Office'}</span>
                 <span className="dashed-line flex-grow h-4 mx-1"></span>
                 <span className="dashed-line w-full h-4 mt-1"></span>
                 <span className="mt-1 flex items-end w-full">
                    <span>{language === 'marathi' ? 'ची विशेष ग्रामसभा सरपंच श्री/सौ.' : "'s Special Gramsabha Sarpanch Mr./Mrs."}</span>
                    <span className="dashed-line flex-grow h-4 mx-1"></span>
                 </span>
                 <span className="mt-1 flex items-end w-full">
                    <span className="dashed-line w-24 h-4 mr-1"></span>
                    <span>{language === 'marathi' ? 'यांच्या अध्यक्षतेखाली ग्रामपंचायत कार्यालयात सकाळी/दुपारी ठीक :' : 'under the chairmanship of, at Gram Panchayat Office exactly at Morning/Afternoon:'}</span>
                    <span className="dashed-line w-8 h-4 mx-2"></span>
                    <span>:</span>
                 </span>
                 <span className="mt-1">{language === 'marathi' ? 'वाजता बोलविण्यात आली.' : 'was convened.'}</span>
              </p>

              <p className="indent-8 mt-1">{language === 'marathi' ? 'या सभेत खालील ठराव मांडण्यात आला.' : 'The following resolution was moved in this meeting.'}</p>

              <p className="font-bold text-[18px] mt-1">
                 {language === 'marathi' ? 'विषय: VB-G-RAM G अधिनियम २०२५ मधील विविध तरतुदी तसेच विकसित भारत @२०४७ या दृष्टीकोनाबाबत चर्चा व मार्गदर्शन करणेबाबत.' : 'Subject: Regarding discussion and guidance on various provisions of VB-G-RAM G Act 2025 as well as the vision of Viksit Bharat @2047.'}
              </p>

              <p className="text-justify indent-8 mt-1">
                 {language === 'marathi' ? 'सुचक यांनी सुचविले की, VB-G-RAM G अधिनियम २०२५ मधील विविध तरतुदी तसेच विकसित भारत @२०४७ या दृष्टीकोनाबाबत चर्चा व मार्गदर्शन करण्यात यावे. सभेत या विषयावर चर्चा करण्यात आली. श्री.' : 'The proposer suggested that there should be discussion and guidance on various provisions in the VB-G-RAM G Act 2025 as well as the vision of Viksit Bharat @2047. This subject was discussed in the meeting. Mr.'}
                 <span className="dashed-line inline-block w-full h-4 mt-1"></span>
                 <span className="dashed-line inline-block w-[300px] h-4 mt-1"></span>
                 <span> {language === 'marathi' ? 'यांनी पुढीलप्रमाणे माहिती दिली.' : 'gave the following information.'}</span>
              </p>

              <p className="font-bold text-[17px] mt-1">{language === 'marathi' ? 'विकसित भारत -रोजगार व आजीविका हमी मिशन (ग्रामीण) VB-G RAM G अधिनियम, २०२५' : 'Viksit Bharat - Employment and Livelihood Guarantee Mission (Rural) VB-G RAM G Act, 2025'}</p>

              <p className="text-justify">{language === 'marathi' ? '१. संसदेद्वारे अलीकडेच मंजूर करण्यात आलेला “विकसित भारत - रोजगार व आजीविका हमी मिशन (ग्रामीण): VB-G RAM G अधिनियम, २०२५” हा देशातील ग्रामीण कुटुंबांसाठी एक ऐतिहासिक टप्पा आहे.' : '1. The recently passed by Parliament “Viksit Bharat - Employment and Livelihood Guarantee Mission (Rural): VB-G RAM G Act, 2025” is a historic milestone for rural families in the country.'}</p>
              
              <p className="text-justify">{language === 'marathi' ? '२. या नव्या अधिनियमाअंतर्गत रोजगाराची हमी १०० दिवसांवरून १२५ दिवसांपर्यंत वाढविण्यात आली असून हा मेहनती ग्रामीण समाजासाठी अत्यंत महत्त्वाचा बदल आहे.' : '2. Under this new act, employment guarantee has been increased from 100 days to 125 days, which is a very important change for the hardworking rural society.'}</p>
              
              <p className="text-justify">{language === 'marathi' ? '३. या अधिनियमाची प्रमुख वैशिष्ट्ये पुढीलप्रमाणे आहेत:' : '3. The main features of this act are as follows:'}</p>

              <p className="font-bold">{language === 'marathi' ? '(i) बेरोजगारी भत्त्याचा हक्क' : '(i) Right to Unemployment Allowance'}</p>
              <p className="text-justify">{language === 'marathi' ? 'पूर्वीच्या महात्मा गांधी नरेगा अधिनियमांतर्गत बेरोजगारी भत्ता मिळणे कठीण होते. मात्र' : 'Under the previous Mahatma Gandhi NREGA Act, getting unemployment allowance was difficult. But'}</p>
           </div>
        </div>

        {/* PAGE 2 */}
        <div className="bg-white relative box-border flex flex-col page pt-12">
           <div className="border border-black border-t-0 p-3 flex-grow flex flex-col relative text-[17px] leading-snug space-y-1">
              <p className="text-justify">{language === 'marathi' ? 'VB-GRAM G अधिनियम, २०२५ अंतर्गत सर्व अडथळे दूर करण्यात आले आहेत. कामाची मागणी करूनही काम न मिळाल्यास बेरोजगारी भत्ता आपोआप देय होईल, त्यामुळे रोजगाराचा हक्क प्रत्यक्षात कायदेशीररीत्या अंमलात येईल.' : 'under VB-GRAM G Act, 2025 all hurdles have been removed. If work is not provided even after demanding work, unemployment allowance will automatically become payable, thereby legally implementing the right to employment.'}</p>

              <p className="font-bold mt-1">{language === 'marathi' ? '(ii) वेळेवर मजुरी अदा' : '(ii) Timely Payment of Wages'}</p>
              <p className="text-justify">{language === 'marathi' ? 'मजुरी अदा करण्यात होणाऱ्या विलंबाच्या तक्रारी लक्षात घेऊन, आता मजुरी देण्यास उशीर झाल्यास प्रत्येक विलंबित दिवसासाठी भरपाई मजुरीसोबत दिली जाईल. यामुळे पारदर्शकता वाढेल.' : 'Keeping in view the complaints of delay in payment of wages, now if there is a delay in payment of wages, compensation will be paid along with wages for each delayed day. This will increase transparency.'}</p>

              <p className="font-bold mt-1">{language === 'marathi' ? '(iii) ग्रामस्तरावर नियोजनाची स्वायत्तता' : '(iii) Autonomy of Planning at Village Level'}</p>
              <p className="text-justify">{language === 'marathi' ? 'आता कामांचे नियोजन ग्रामसभेतच ठरविले जाईल. ग्रामपंचायत विकसित ग्रामपंचायत आराखडा तयार करेल आणि त्यात ग्रामस्थांचा सहभाग बंधनकारक असेल.' : 'Now the planning of works will be decided in the Gramsabha itself. The Gram Panchayat will prepare a Viksit Gram Panchayat Plan and the participation of villagers in it will be mandatory.'}</p>

              <p className="font-bold mt-1">{language === 'marathi' ? '(iv) कामांच्या चार प्रमुख श्रेणी' : '(iv) Four Major Categories of Works'}</p>
              <p>{language === 'marathi' ? '• जलसुरक्षा व जलसंवर्धनाशी संबंधित कामे' : '• Water security and water conservation related works'}</p>
              <p>{language === 'marathi' ? '• ग्रामीण पायाभूत सुविधांशी संबंधित कामे' : '• Rural infrastructure related works'}</p>
              <p>{language === 'marathi' ? '• आजीविका संवर्धनाची कामे' : '• Livelihood enhancement works'}</p>
              <p>{language === 'marathi' ? '• हवामान बदल व प्रतिकूल हवामानाशी संबंधित कामे' : '• Climate change and extreme weather related works'}</p>
              <p className="text-justify mt-1">{language === 'marathi' ? 'ही चारही क्षेत्रे विकास, सशक्तीकरण, अभिसरण व शाश्वत आजीविकेचा पाया ठरतील.' : 'These four areas will be the foundation of development, empowerment, convergence and sustainable livelihood.'}</p>

              <p className="font-bold mt-1">{language === 'marathi' ? '(v) विभागीय समन्वय व पारदर्शकता' : '(v) Departmental Coordination and Transparency'}</p>
              <p className="text-justify">{language === 'marathi' ? 'सर्व ग्रामस्तरीय कामे विकसित भारत राष्ट्रीय ग्रामीण पायाभूत सुविधा स्टॅकमध्ये समाविष्ट केली जातील. यामुळे कामांची पुनरावृत्ती टळेल, विभागांतील समन्वय वाढेल व भ्रष्टाचारास आळा बसेल.' : 'All village level works will be included in the Viksit Bharat National Rural Infrastructure Stack. This will avoid duplication of works, increase coordination among departments and curb corruption.'}</p>

              <p className="font-bold mt-1">{language === 'marathi' ? '(vi) शेती - मजूर समन्वय' : '(vi) Agriculture - Labour Coordination'}</p>
              <p className="text-justify">{language === 'marathi' ? 'राज्य सरकारला बियाणे पेरणी व कापणीच्या हंगामात एकूण ६० दिवस अधिसूचित करण्याचा अधिकार असेल. या कालावधीत या अधिनियमांतर्गत कामे घेतली जाणार नाहीत, त्यामुळे शेतीकामे सुरळीत होतील.' : 'The State Government will have the power to notify a total of 60 days during the seed sowing and harvesting season. During this period no work will be undertaken under this Act, thereby facilitating smooth agricultural operations.'}</p>

              <p className="font-bold mt-1">{language === 'marathi' ? '(vii) प्रशासकीय खर्च मर्यादेत वाढ' : '(vii) Increase in Administrative Expenditure Limit'}</p>
              <p className="text-justify">{language === 'marathi' ? 'प्रशासकीय खर्चाची मर्यादा ६% वरून ९% करण्यात आली आहे. यामुळे क्षेत्रीय कर्मचाऱ्यांच्या सेवा, प्रशिक्षण व क्षमतावृद्धीस मदत होईल.' : 'The administrative expenditure limit has been increased from 6% to 9%. This will help in services, training and capacity building of field staff.'}</p>

              <p className="font-bold mt-1">{language === 'marathi' ? '(viii) अधिकृत माहितीचे स्रोत' : '(viii) Sources of Official Information'}</p>
           </div>
        </div>

        {/* PAGE 3 */}
        <div className="bg-white relative box-border flex flex-col page pt-12">
           <div className="border border-black border-t-0 p-3 flex-grow flex flex-col relative text-[17px] leading-snug space-y-2">
              <p className="text-justify">{language === 'marathi' ? 'या अधिनियमाबाबतची माहिती फक्त अधिकृत शासकीय स्रोतांमधून घ्यावी. आवश्यक माहिती पंचायत कार्यालयातून मिळू शकते.' : 'Information regarding this Act should be obtained only from official government sources. Necessary information can be obtained from the Panchayat office.'}</p>

              <p className="font-bold mt-2">{language === 'marathi' ? '(ix) सुरू असलेली मनरेगा कामे सुरक्षित' : '(ix) Ongoing MGNREGA Works Protected'}</p>
              <p className="text-justify">{language === 'marathi' ? 'मनरेगा अंतर्गत सध्या सुरू असलेली सर्व कामे सुरूच राहतील. कोणतेही काम थांबणार नाही. VB-GRAM G अधिनियम लागू झाल्यानंतर नवीन कामे या अधिनियमांतर्गत सुरू होतील. म्हणजेच अडथळा नाही, तर अधिक संधी.' : 'All ongoing works under MGNREGA will continue. No work will be stopped. New works will start under VB-GRAM G Act after it comes into force. Meaning no hurdle, but more opportunities.'}</p>

              <p className="font-bold mt-2">{language === 'marathi' ? '(x) वाढीव लाभ' : '(x) Enhanced Benefits'}</p>
              <p className="text-justify">{language === 'marathi' ? 'VB—G RAM G अधिनियम, २०२५ पूर्णपणे लागू झाल्यानंतर लाभार्थ्यांना १२५ दिवसांचा रोजगार तसेच वाढीव मजुरी दरांचा लाभ मिळेल.' : 'After full implementation of VB-G RAM G Act, 2025, beneficiaries will get the benefit of 125 days employment as well as increased wage rates.'}</p>

              <div className="mt-8 flex justify-center">
                 <p className="text-[17px] indent-8">{language === 'marathi' ? 'वरीलप्रमाणे माहिती सभेत देण्यात आली व ठराव बहुमताने/सर्वानुमते संमत करण्यात आला.' : 'The above information was given in the meeting and the resolution was passed by majority/unanimously.'}</p>
              </div>

              <div className="mt-8 space-y-4 text-[17px]">
                 <p className="flex items-end">
                    <span className="mr-2">{language === 'marathi' ? 'सुचक:' : 'Proposer:'}</span>
                    <span className="dashed-line w-[300px] h-4"></span>
                 </p>
                 <p className="flex items-end">
                    <span className="mr-2">{language === 'marathi' ? 'अनुमोदन:' : 'Seconder:'}</span>
                    <span className="dashed-line w-[300px] h-4"></span>
                 </p>
              </div>

              <div className="flex justify-between mt-auto pt-24 px-4 pb-12">
                 <div className="flex flex-col items-start w-[200px]">
                    <p className="font-bold text-lg mb-8">{language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</p>
                    <span className="dashed-line w-full h-4"></span>
                 </div>
                 <div className="flex flex-col items-center">
                    <p className="font-bold text-lg mb-8">{language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer'}</p>
                    <div className="flex items-end w-[250px]">
                       <span className="mr-2">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
                       <span className="dashed-line flex-grow h-4"></span>
                    </div>
                 </div>
              </div>
           </div>

           <div className="absolute bottom-6 left-14 text-blue-700 text-sm underline print:hidden border-t-0">
             www.majhigrampanchayat.com
           </div>
        </div>
      </div>
    </div>
  );
};

export default VbGramWordResolutionForm;
